import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { api } from "@/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProductsTab } from "./ProductsTab";
import { BulkUpload } from "./BulkUpload";
import { CategoryManager } from "./CategoryManager";
import { Messages } from "./Messages";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  const refresh = useCallback(async () => {
    const [p, c] = await Promise.all([api.get("/products"), api.get("/categories")]);
    setProducts(p.data);
    setCategories(c.data);
  }, []);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        refresh().catch(() => toast.error("Veriler yüklenemedi"));
      })
      .catch(() => {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      });
  }, [navigate, refresh]);

  const logout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  if (!user) return <div className="min-h-screen bg-beige-soft" data-testid="admin-loading" />;

  return (
    <div className="min-h-screen bg-beige-soft" data-testid="admin-dashboard">
      <header className="bg-white border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="font-serif text-2xl text-stone-900 tracking-tight">İnegöl <span className="text-wood-light">Mobilya</span> <span className="text-xs font-sans font-semibold tracking-[0.25em] uppercase text-stone-500 ml-2">Yönetim</span></span>
          <div className="flex items-center gap-5">
            <a href="/" className="text-xs text-stone-500 link-underline" data-testid="admin-view-site">Siteyi Görüntüle</a>
            <button onClick={logout} className="flex items-center gap-2 text-xs font-semibold tracking-[0.15em] uppercase text-stone-600 hover:text-stone-900" data-testid="admin-logout-button">
              <LogOut size={14} /> Çıkış
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <Tabs defaultValue="products">
          <TabsList className="bg-white border border-stone-200 rounded-none mb-8" data-testid="admin-tabs">
            <TabsTrigger value="products" data-testid="tab-products">Ürünler ({products.length})</TabsTrigger>
            <TabsTrigger value="upload" data-testid="tab-upload">Toplu Yükleme</TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories">Kategoriler</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages">Mesajlar</TabsTrigger>
          </TabsList>
          <TabsContent value="products">
            <ProductsTab products={products} categories={categories} onChange={refresh} />
          </TabsContent>
          <TabsContent value="upload">
            <BulkUpload categories={categories} onDone={refresh} />
          </TabsContent>
          <TabsContent value="categories">
            <CategoryManager categories={categories} onChange={refresh} />
          </TabsContent>
          <TabsContent value="messages">
            <Messages />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
